import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== Centralized Axios instance with retry logic =====
const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
});

// Retry interceptor: retry failed requests up to 2 times (network errors + 5xx)
api.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (!config) return Promise.reject(error);

  config._retryCount = config._retryCount || 0;
  const isRetryable =
    !error.response ||                          // Network error
    (error.response.status >= 500 && error.response.status < 600);  // Server error

  if (isRetryable && config._retryCount < 2 && !config._skipAuthRetry) {
    config._retryCount += 1;
    await new Promise((r) => setTimeout(r, 1000 * config._retryCount)); // backoff
    return api(config);
  }
  return Promise.reject(error);
});

// Default export: the configured instance (used by AuthContext)
export default api;

// ===== API helper functions =====

// Tasks
export const getTasks = (params = {}) => api.get(`${API}/tasks`, { params });
export const getTasksLightweight = (params = {}) => api.get(`${API}/tasks/list`, { params });
export const getTask = (id) => api.get(`${API}/tasks/${id}`);
export const getTaskCounts = () => api.get(`${API}/tasks/counts/summary`);
export const createTask = (data) => api.post(`${API}/tasks`, data);
export const updateTask = (id, data) => api.put(`${API}/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`${API}/tasks/${id}`);
export const assignTask = (id, designer_id) => api.post(`${API}/tasks/${id}/assign`, { designer_id });
export const assignCustomer = (id, customer_id) => api.post(`${API}/tasks/${id}/assign-customer`, { customer_id });
export const updateTaskStatus = (id, data) => api.post(`${API}/tasks/${id}/status`, data);
export const requestRevision = (id, remarks) => api.post(`${API}/tasks/${id}/revision`, null, { params: { remarks } });
export const getTaskHistory = (id) => api.get(`${API}/tasks/${id}/history`);

// AI Instruction Parser
export const aiParseInstructions = (instruction) => api.post(`${API}/tasks/ai-parse-instructions`, { instruction });

// New Workflow Endpoints
export const salesAssignTask = (id, customer_id, design_head_id, remarks = null) =>
  api.post(`${API}/tasks/${id}/sales-assign`, { customer_id, design_head_id, remarks });
export const designerStartWork = (id) => api.post(`${API}/tasks/${id}/start-work`);
export const designerSubmitWork = (id, remarks = null) =>
  api.post(`${API}/tasks/${id}/designer-submit`, { remarks });
export const clientRequestRevision = (id, data) =>
  api.post(`${API}/tasks/${id}/client-revision`, data);

// Approval Workflow
export const cadHeadApprove = (id) => api.post(`${API}/tasks/${id}/cad-head-approve`);
export const designHeadApprove = (id) => api.post(`${API}/tasks/${id}/design-head-approve`);
export const sendForClientApproval = (id) => api.post(`${API}/tasks/${id}/send-for-client-approval`);
export const clientApprove = (id) => api.post(`${API}/tasks/${id}/client-approve`);

// Users
export const getUsers = () => api.get(`${API}/users`);
export const getPendingUsers = () => api.get(`${API}/users/pending`);
export const getDesigners = () => api.get(`${API}/users/designers`);
export const getDesignHeads = () => api.get(`${API}/users/design-heads`);
export const getCustomers = () => api.get(`${API}/users/customers`);
export const createUser = (data) => api.post(`${API}/users`, data);
export const updateUser = (id, data) => api.put(`${API}/users/${id}`, data);
export const deleteUser = (id) => api.delete(`${API}/users/${id}`);
export const approveUser = (id) => api.post(`${API}/users/${id}/approve`);
export const rejectUser = (id) => api.post(`${API}/users/${id}/reject`);
export const suspendUser = (id) => api.post(`${API}/users/${id}/suspend`);

// Auth
export const forgotPassword = (email) => api.post(`${API}/auth/forgot-password`, { email });
export const resetPassword = (token, new_password) => api.post(`${API}/auth/reset-password`, { token, new_password });

// Analytics
export const getAnalytics = () => api.get(`${API}/analytics`);
export const getCadAnalytics = () => api.get(`${API}/analytics/cad`);

// Audit Logs
export const getAuditLogs = (params = {}) => api.get(`${API}/audit-logs`, { params });

// Messages
export const getMessages = (taskId, search = null) => {
  const params = search ? { search } : {};
  return api.get(`${API}/tasks/${taskId}/messages`, { params });
};
export const sendMessage = (taskId, message, attachments = []) =>
  api.post(`${API}/tasks/${taskId}/messages`, { task_id: taskId, message, attachments });

// Upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/upload-file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Helper to get full image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
  return `${BACKEND_URL}${imagePath}`;
};

// Stone Template
export const downloadStoneTemplate = () => {
  const token = localStorage.getItem('token');
  const url = `${API}/templates/stone-entry?token=${token}`;
  window.open(url, '_blank');
};

export const uploadStoneData = (taskId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/tasks/${taskId}/upload-stones`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// QC Checklist
export const updateQCChecklist = (taskId, data) => api.post(`${API}/tasks/${taskId}/qc-checklist`, data);
export const getQCStatus = (taskId) => api.get(`${API}/tasks/${taskId}/qc-status`);

// Quotation Engine
export const getLaborRates = (params = {}) => api.get(`${API}/quotation/labor-rates`, { params });
export const createLaborRate = (data) => api.post(`${API}/quotation/labor-rates`, data);
export const updateLaborRate = (id, data) => api.put(`${API}/quotation/labor-rates/${id}`, data);
export const deleteLaborRate = (id) => api.delete(`${API}/quotation/labor-rates/${id}`);
export const importLaborRates = (file, replaceExisting = false) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`${API}/quotation/import-labor-rates?replace_existing=${replaceExisting}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getProcessCodes = () => api.get(`${API}/quotation/process-codes`);
export const getItemCodes = (processCode = null) => {
  const params = processCode ? { process_code: processCode } : {};
  return api.get(`${API}/quotation/item-codes`, { params });
};
export const calculateQuotation = (data) => api.post(`${API}/quotation/calculate`, data);
export const getQuotations = (params = {}) => api.get(`${API}/quotation/quotations`, { params });
export const getQuotation = (id) => api.get(`${API}/quotation/quotations/${id}`);
export const deleteQuotation = (id) => api.delete(`${API}/quotation/quotations/${id}`);
export const exportQuotation = (id, format = 'excel') => {
  const token = localStorage.getItem('token');
  const url = `${API}/quotation/export/${id}?format=${format}&token=${token}`;
  window.open(url, '_blank');
};

// Simplified Workflow
export const moveToQC = (id) => api.post(`${API}/tasks/${id}/move-to-qc`);
export const sendToClient = (id) => api.post(`${API}/tasks/${id}/send-to-client`);
export const approveTask = (id) => api.post(`${API}/tasks/${id}/approve`);
export const requestRevisionClone = (id, data) => api.post(`${API}/tasks/${id}/request-revision`, data);
export const getTaskRevisions = (id) => api.get(`${API}/tasks/${id}/revisions`);

// Client Account Management
export const createClientAccount = (data) => api.post(`${API}/users/create-client`, data);

// Client Masters
export const getClientMasters = () => api.get(`${API}/client-masters`);
export const getClientMaster = (id) => api.get(`${API}/client-masters/${id}`);
export const createClientMaster = (data) => api.post(`${API}/client-masters`, data);
export const updateClientMaster = (id, data) => api.put(`${API}/client-masters/${id}`, data);
export const deleteClientMaster = (id) => api.delete(`${API}/client-masters/${id}`);

// Enhanced Quotations
export const saveFullQuotation = (data) => api.post(`${API}/quotation/save-full`, data);
export const getSavedQuotations = (params = {}) => api.get(`${API}/quotation/saved`, { params });
export const getSavedQuotation = (id) => api.get(`${API}/quotation/saved/${id}`);
export const deleteSavedQuotation = (id) => api.delete(`${API}/quotation/saved/${id}`);

// Style No lookups
export const getTaskByStyleNo = (styleNo) => api.get(`${API}/tasks/by-style/${encodeURIComponent(styleNo)}`);
export const getQuotationsByStyleNo = (styleNo) => api.get(`${API}/quotations/by-style/${encodeURIComponent(styleNo)}`);

// Sync CAD sheet data back to task
export const syncCadToTask = (taskId, cadData) => api.put(`${API}/tasks/${taskId}`, cadData);

// Diamond Master Data
export const getDiamondRates = (params = {}) => api.get(`${API}/quotation/diamond-rates`, { params });
export const getDiamondShapes = () => api.get(`${API}/quotation/diamond-shapes`);
export const getDiamondQualities = (shape) => api.get(`${API}/quotation/diamond-qualities`, { params: { shape } });
export const diamondRateLookup = (shape, rm_code, carat_weight) =>
  api.get(`${API}/quotation/diamond-rate-lookup`, { params: { shape, rm_code, carat_weight } });
export const settingRateLookup = (stone_shape, setting_type, weight_ct = 0) =>
  api.get(`${API}/quotation/setting-rate-lookup`, { params: { stone_shape, setting_type, weight_ct } });
export const getPointerLookup = (mm_size, shape = 'Round') =>
  api.get(`${API}/quotation/pointer-lookup`, { params: { mm_size, shape } });
export const getPointerTable = (shape = 'Round') =>
  api.get(`${API}/quotation/pointer-table`, { params: { shape } });
export const getMasterSummary = () => api.get(`${API}/quotation/master-summary`);

// CAD Sheet CRUD
export const createCadSheet = (data) => api.post(`${API}/cad-sheets`, data);
export const listCadSheets = (params = {}) => api.get(`${API}/cad-sheets`, { params });
export const getCadSheet = (id) => api.get(`${API}/cad-sheets/${id}`);
export const updateCadSheet = (id, data) => api.put(`${API}/cad-sheets/${id}`, data);
export const deleteCadSheet = (id) => api.delete(`${API}/cad-sheets/${id}`);
export const bulkDeleteCadSheets = (ids) => api.delete(`${API}/cad-sheets`, { data: ids });

// Portal Import
export const getPortals = () => api.get(`${API}/portals`);
export const createPortal = (data) => api.post(`${API}/portals`, data);
export const updatePortal = (id, data) => api.put(`${API}/portals/${id}`, data);
export const deletePortal = (id) => api.delete(`${API}/portals/${id}`);
export const triggerPortalImport = (data) => api.post(`${API}/portals/import`, data);
export const aiEnhanceImport = (importId, orderIdx) => api.post(`${API}/portals/import/ai-enhance?import_id=${importId}&order_idx=${orderIdx}`);
export const confirmImport = (data) => api.post(`${API}/portals/import/confirm`, data);
export const getPortalImports = () => api.get(`${API}/portals/imports`);

// Cached customer list
let _customerCache = null;
let _customerCacheTime = 0;
const CUSTOMER_CACHE_TTL = 5 * 60 * 1000;

export const getCustomersCached = async () => {
  const now = Date.now();
  if (_customerCache && (now - _customerCacheTime) < CUSTOMER_CACHE_TTL) return _customerCache;
  const res = await api.get(`${API}/customers`);
  _customerCache = res.data;
  _customerCacheTime = now;
  return _customerCache;
};

export const invalidateCustomerCache = () => { _customerCache = null; };
